import axios from 'axios';
import { getBaseURL } from '@scoutus/sports-metrics';

const client = axios.create();

let authToken: string | null = null;

export function setClipCreatorAnalyticsAuthToken(token: string | null): void {
  authToken = token;
}

client.interceptors.request.use((config) => {
  config.baseURL = getBaseURL();
  if (authToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

export type ClipPlanFilterParam = 'all' | 'free' | 'premium';

export type ClipGranularityParam = 'day' | 'week';

export interface ClipKpiTotalsResponse {
  current: number;
  prior: number | null;
}

export interface ClipRenderPointResponse {
  date: string;
  renders: number;
  premiumRenders: number;
  freeRenders: number;
  renderers: number;
}

export interface ClipPublishPointResponse {
  date: string;
  all: number;
  post: number;
  story: number;
  highlight: number;
  chat: number;
}

export interface ClipCreatorUsageResponse {
  kpis: {
    renders: ClipKpiTotalsResponse;
    premiumRenders: ClipKpiTotalsResponse;
    freeRenders: ClipKpiTotalsResponse;
    renderers: ClipKpiTotalsResponse;
  };
  renders: ClipRenderPointResponse[];
  publishes: ClipPublishPointResponse[];
}

export async function getClipCreatorUsage(params: {
  start?: string;
  end?: string;
  granularity: ClipGranularityParam;
  plan: ClipPlanFilterParam;
  userId?: number;
}): Promise<ClipCreatorUsageResponse> {
  const response = await client.get<{ data: ClipCreatorUsageResponse }>(
    '/api/v1/admin/analytics/clip-creator/usage',
    { params },
  );
  return response.data.data;
}

export type ClipRenderStatusParam = 'started' | 'completed' | 'failed' | 'cancelled';

export interface ClipRenderListParams {
  start?: string;
  end?: string;
  plan: ClipPlanFilterParam;
  userId?: number;
  type: 'all' | 'render' | 'flatten';
  status: 'all' | ClipRenderStatusParam;
  quality: 'all' | '1080p' | '720p' | '480p';
  renderTime: 'all' | 'lt30' | '30to120' | 'gt120';
  page: number;
  pageSize: number;
}

export interface ClipCreatorUserRefResponse {
  userId: number;
  adminId: number;
  name: string;
  userType: string | null;
  profileImage: string | null;
}

export interface ClipRenderRowResponse {
  id: number;
  user: ClipCreatorUserRefResponse;
  plan: string;
  type: string | null;
  status: string | null;
  quality: string | null;
  watermark: boolean | null;
  durationSec: number | null;
  renderMs: number | null;
  error: string | null;
  progressPct: number | null;
  media: { mediaId: number; title: string } | null;
  createdAt: string;
}

export interface ClipRenderUserRowResponse {
  user: ClipCreatorUserRefResponse;
  lastPlan: string;
  renders: number;
  flattens: number;
  published: number;
  lastRenderAt: string;
}

export interface ClipPageResponse<T> {
  rows: T[];
  total: number;
}

export interface ClipCreatorOutcomesResponse {
  counts: { started: number; completed: number; failed: number; cancelled: number };
  series: { date: string; completed: number; failed: number; cancelled: number }[];
}

export interface ClipCreatorMediaResponse {
  mediaId: number;
  user: ClipCreatorUserRefResponse;
  title: string;
  mimeType: string;
  sizeBytes: number | null;
  provider: string;
  uploadStatus: string;
  moderationStatus: string;
  url: string | null;
  createdAt: string;
  activities: { activityId: string; kind: 'post' | 'story' | 'unknown'; text: string | null; createdAt: string }[];
  profile: { userId: number; createdAt: string }[];
  messages: { messageId: string; conversationId: string; createdAt: string }[];
}

export async function getClipCreatorOutcomes(params: {
  start?: string;
  end?: string;
  granularity: ClipGranularityParam;
  plan: ClipPlanFilterParam;
  userId?: number;
}): Promise<ClipCreatorOutcomesResponse> {
  const response = await client.get<{ data: ClipCreatorOutcomesResponse }>(
    '/api/v1/admin/analytics/clip-creator/outcomes',
    { params },
  );
  return response.data.data;
}

export async function getClipRenders(
  params: ClipRenderListParams & { sort: 'newest' | 'oldest' | 'renderTime' | 'duration' },
): Promise<ClipPageResponse<ClipRenderRowResponse>> {
  const response = await client.get<{ data: ClipPageResponse<ClipRenderRowResponse> }>(
    '/api/v1/admin/analytics/clip-creator/renders',
    { params },
  );
  return response.data.data;
}

export async function getClipRenderUsers(
  params: ClipRenderListParams & { sort: 'renders' | 'flattens' | 'published' | 'recent' },
): Promise<ClipPageResponse<ClipRenderUserRowResponse>> {
  const response = await client.get<{ data: ClipPageResponse<ClipRenderUserRowResponse> }>(
    '/api/v1/admin/analytics/clip-creator/renders/by-user',
    { params },
  );
  return response.data.data;
}

export async function getClipCreatorMedia(mediaId: number): Promise<ClipCreatorMediaResponse> {
  const response = await client.get<{ data: ClipCreatorMediaResponse }>(
    `/api/v1/admin/analytics/clip-creator/media/${mediaId}`,
  );
  return response.data.data;
}
